import { path, comparator } from 'ramda';

import {
  CAD_FILE_TYPES,
  IMG_FILE_TYPES,
  PDF_FILE_TYPES,
  VECTOR_EDITOR_FILE_TYPES,
  CONVERTIBLE_FILES_TYPES,
  UNITS,
  VIDEO_FILE_TYPES,
} from 'constants/all';
import { KG_PER_LB } from 'constants/all';

import { formatFloat } from '../formatters';

const IN_TO_MM = 25.4;
const PRECISION = 5;

/**
 * Convert millimeters to inches
 * @param mm
 * @param power - Exponent of units, e.g. power == 2 means
 * @param precision - Decimal precision, e.g. precision == 2 means X.YY
 * that we convert mm^2 to inch^2
 */
export const mmToInches = (mm, power = 1, precision = PRECISION) => {
  const inchesFloat = mm / Math.pow(IN_TO_MM, power);
  return +(Math.round(inchesFloat + 'e+' + precision) + 'e-' + precision);
};

export const inchesToMm = (inches, power = 1) => {
  const mmFloat = inches * Math.pow(IN_TO_MM, power);
  return +(Math.round(mmFloat + 'e+' + PRECISION) + 'e-' + PRECISION);
};

export const poundsToKilograms = (weightInPounds) => {
  return weightInPounds * KG_PER_LB;
};

export const getImperialUnits = (dimension, units, decimalPlaces = 2) => {
  switch (units) {
    case UNITS.IN:
      return formatFloat(dimension, decimalPlaces);
    case UNITS.MM:
      return formatFloat(mmToInches(dimension, 1), decimalPlaces);
    default:
      return undefined;
  }
};

export const getMetricUnits = (dimension, units, decimalPlaces = 2) => {
  switch (units) {
    case UNITS.IN:
      return formatFloat(dimension * IN_TO_MM, decimalPlaces);
    case UNITS.MM:
      return formatFloat(dimension, decimalPlaces);
    default:
      return undefined;
  }
};

export const wait = (ms) => new Promise((res) => setTimeout(res, ms));

export const isBlank = (value) => ['', null, undefined].indexOf(value) !== -1;

/**
 * Mimics the os.path.basename function from python. Works for both windows
 * and non-windows file paths e.g.
 * console.log(basename('C:\\Users\\user\\file.txt')); // Outputs: 'file.txt'
 * console.log(basename('/home/user/file.txt')); // Outputs: 'file.txt'
 * @param path: string of file path
 * @returns the basename from the file
 */
export const getFilePathBasename = (path) => {
  return path.split(/[/\\]/).pop();
};

const CREO_EXTENSIONS = ['asm', 'prt'];
export const getFileNameWithOutExtension = (name) => {
  const splits = name.split('.');
  if (
    splits.length > 2 &&
    CREO_EXTENSIONS.includes(splits[splits.length - 2])
  ) {
    splits.splice(-2, 2);
    return splits.join('.');
  }
  if (splits.length > 1) splits.splice(-1, 1);
  return splits.join('.');
};

export const getFileExtension = (name) => {
  const splits = name.split('.');
  if (
    splits.length > 2 &&
    CREO_EXTENSIONS.includes(splits[splits.length - 2])
  ) {
    return splits[splits.length - 2].toLowerCase();
  }
  return splits.length > 1 ? splits[splits.length - 1].toLowerCase() : '';
};

export const createComparator =
  (comp) =>
  (orderByIn = null) => {
    let isDescending = false;
    let orderBy = orderByIn;
    if (typeof orderByIn === 'string' && orderByIn[0] === '-') {
      isDescending = true;
      orderBy = orderByIn.slice(1);
    }
    const orderByGetter =
      typeof orderByIn === 'string' ? path(orderBy.split('.')) : undefined;
    return isDescending
      ? //descending cases: a and b arguments are swapped because descending
        orderByGetter
        ? comparator((a, b) => comp(orderByGetter(b), orderByGetter(a)))
        : comparator((a, b) => comp(b, a))
      : //ascending cases
      orderByGetter
      ? comparator((a, b) => comp(orderByGetter(a), orderByGetter(b)))
      : comparator(comp);
  };

export const createNumericComparator = createComparator(
  (a, b) => parseFloat(a) < parseFloat(b),
);

export const createAlphabeticalComparator = createComparator(
  (a, b) => a.toLowerCase() < b.toLowerCase(),
);

export const createMomentComparator = createComparator((a, b) => a.isBefore(b));

export const isPrimitiveType = (a) => Object(a) !== a;

export const isString = (a) => typeof a === 'string';

export const isObject = (a) => typeof a === 'object' && a !== null;

export const defer = () => {
  const deferred = {};
  deferred.promise = new Promise((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });
  return deferred;
};

export const validatePassword = (password = '') => {
  return {
    minLength: password.length < 15,
  };
};

export const isValidPassword = (password) => {
  const vals = validatePassword(password);
  return Object.values(vals).every((v) => v === false);
};

export const validateConfirmPassword = (current, password) => {
  return (current ?? '') === password;
};

export const isFileCAD = (fileExt) =>
  Object.values(CAD_FILE_TYPES).includes(fileExt);

export const isFileVector = (fileExt) =>
  fileExt === CAD_FILE_TYPES.DWG || fileExt === CAD_FILE_TYPES.DXF;

export const isFileDxf = (fileExt) => fileExt === CAD_FILE_TYPES.DXF;

export const isFileDwg = (fileExt) => fileExt === CAD_FILE_TYPES.DWG;

export const isFileImg = (fileExt) =>
  Object.values(IMG_FILE_TYPES).includes(fileExt);

export const isFilePDF = (fileExt) =>
  Object.values(PDF_FILE_TYPES).includes(fileExt);

export const isFileVectorizable = (fileExt) =>
  Object.values(VECTOR_EDITOR_FILE_TYPES).includes(fileExt);

export const isFileConvertible = (fileExt) =>
  Object.values(CONVERTIBLE_FILES_TYPES).includes(fileExt);

export const isFileVideo = (fileExt) =>
  Object.values(VIDEO_FILE_TYPES).includes(fileExt);
